import React, { useEffect, useState, useCallback } from 'react';
import {
  GlobeAltIcon,
  PencilSquareIcon,
  EyeIcon,
  HomeIcon,
  InformationCircleIcon,
  WrenchScrewdriverIcon,
  PhoneIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  CloudArrowDownIcon,
  SparklesIcon,
  LightBulbIcon,
  BookOpenIcon,
  QuestionMarkCircleIcon,
  ClockIcon,
  ChartBarIcon,
  RocketLaunchIcon,
  ExclamationTriangleIcon,
  CheckIcon,
  XMarkIcon,
  ArrowTopRightOnSquareIcon,
  Bars3Icon,
  MagnifyingGlassIcon,
  AdjustmentsHorizontalIcon,
  StarIcon,
  PhotoIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolid, StarIcon as StarSolid } from '@heroicons/react/24/solid';
import { pageService, PageContent } from '../services/pageService';
import RichTextEditor from '../components/CMS/RichTextEditor';
import InlineRichTextEditor from '../components/CMS/InlineRichTextEditor';
import ImageUploader from '../components/CMS/ImageUploader';
import LivePreview from '../components/CMS/LivePreview';

// Toast Notification Component
interface ToastProps {
  message: string;
  type: 'success' | 'error' | 'info' | 'warning';
  onClose: () => void;
}

const Toast: React.FC<ToastProps> = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 4000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const styles = {
    success: 'bg-green-500 text-white',
    error: 'bg-red-500 text-white',
    info: 'bg-blue-500 text-white',
    warning: 'bg-amber-500 text-white',
  };

  const icons = {
    success: <CheckCircleSolid className="h-5 w-5" />,
    error: <XCircleIcon className="h-5 w-5" />,
    info: <LightBulbIcon className="h-5 w-5" />,
    warning: <ExclamationTriangleIcon className="h-5 w-5" />,
  };


  return (
    <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-5 py-4 rounded-xl shadow-2xl ${styles[type]} animate-slide-up`}>
      {icons[type]}
      <span className="font-medium">{message}</span>
      <button onClick={onClose} className="ml-2 hover:opacity-70"><XMarkIcon className="h-4 w-4" /></button>
    </div>
  );
};

// Progress Bar Component
const ProgressBar: React.FC<{ progress: number; label?: string }> = ({ progress, label }) => (
  <div className="w-full">
    {label && <div className="flex justify-between text-xs text-gray-600 mb-1"><span>{label}</span><span>{progress}%</span></div>}
    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
      <div className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
    </div>
  </div>
);

// Interfaces
interface WebsiteSection { id: string; type: string; title?: string; subtitle?: string; description?: string; content?: string; image?: string; items?: WebsiteSectionItem[]; features?: string[]; order: number; }
interface WebsiteSectionItem { id: string; title: string; description: string; icon?: string; image?: string; link?: string; features?: string[]; order: number; }
interface WebsitePageContent { pageSlug: string; pageTitle: string; sections: WebsiteSection[]; seo: { metaTitle: string; metaDescription: string; keywords: string[]; ogTitle: string; ogDescription: string; }; }
interface WebsiteContactInfo { type: string; label: string; value: string; icon: string; order: number; active: boolean; }

// Website Content Data - Full content from actual website pages
const WEBSITE_CONTENT: Record<string, WebsitePageContent> = {
  'home': {
    pageSlug: 'home', pageTitle: 'Home Page',
    sections: [
      { 
        id: 'search-form', 
        type: 'search-form', 
        title: 'Find My Ride', 
        subtitle: 'Book Your Car', 
        description: 'Search for available cars by selecting pickup and drop-off locations, dates, and times.',
        content: 'Our easy-to-use booking form allows you to quickly find and reserve the perfect car for your needs. Simply select your pickup location (East Perth, Crown Plaza, or Perth Airport), choose your pickup and drop-off dates and times, and click "Find My Ride" to see all available vehicles. We offer flexible rental periods from a few hours to several weeks.',
        order: 1, 
        items: [
          { id: 'pickup-locations', title: 'Pickup Locations', description: 'East Perth, Crown Plaza, Perth Airport', order: 1 },
          { id: 'dropoff-locations', title: 'Drop-off Locations', description: 'East Perth, Crown Plaza, Perth Airport', order: 2 }
        ] 
      },
      { 
        id: 'hero', 
        type: 'hero', 
        title: 'Drive Your Journey', 
        subtitle: 'Leave the Rest to Us!', 
        description: 'We have many types of cars that are ready for you to travel anywhere and anytime.',
        content: 'We have many types of cars that are ready for you to travel anywhere and anytime.',
        image: 'img/home_car.png', 
        order: 2, 
        items: [
          { id: 'cta-contact', title: 'Get In Touch', description: 'Contact us for inquiries and custom rental solutions', link: '/contact-us', order: 1 },
          { id: 'cta-cars', title: 'Our Cars', description: 'Browse our complete fleet of quality rental vehicles', link: '/carslist', order: 2 }
        ] 
      },
      { 
        id: 'top-cars', 
        type: 'car-showcase', 
        title: 'Explore our best-selling rental cars', 
        subtitle: 'POPULAR CAR', 
        description: 'We present popular cars that are rented by customers to maximize your comfort on long trips.',
        content: 'Our carefully curated fleet features the most popular and reliable vehicles in Perth. Each car is meticulously maintained, thoroughly cleaned, and equipped with modern features to ensure your comfort and safety. From fuel-efficient compact cars to spacious family SUVs, we have options to suit every need and budget. All vehicles come with comprehensive insurance options and 24/7 roadside assistance.',
        order: 3, 
        items: [
          { id: 'suzuki-swift', title: 'Suzuki Swift', description: 'Compact automatic car perfect for city driving. Fuel-efficient and easy to park, the Swift is ideal for solo travelers or couples exploring Perth. Features include air conditioning, GPS navigation, Bluetooth connectivity, reverse camera, and cruise control. Great fuel economy makes it perfect for both short trips and longer journeys.', icon: 'car', image: 'img/car_1.png', order: 1, features: ['Air Conditioning', 'GPS Navigation', 'Bluetooth', 'Reverse Camera', 'Cruise Control', 'Automatic Transmission', 'Fuel Efficient'] },
          { id: 'outlander-7-seater', title: 'Outlander 7 Seater', description: 'Spacious 7-seater SUV ideal for family trips and group travel. The Mitsubishi Outlander offers plenty of room for passengers and luggage, making it perfect for airport transfers or road trips across Western Australia. Features include air conditioning, GPS navigation, Bluetooth, reverse camera, cruise control, and third-row seating.', icon: 'car', image: 'img/car_2.png', order: 2, features: ['Air Conditioning', 'GPS Navigation', 'Bluetooth', 'Reverse Camera', 'Cruise Control', '7 Seats', 'Large Boot Space', 'All-Wheel Drive'] },
          { id: 'mazda-cx3', title: 'Mazda CX3', description: 'Stylish compact SUV with premium features and sporty handling. The CX3 combines the practicality of an SUV with the agility of a hatchback. Perfect for those who want a bit more space without sacrificing driving dynamics. Includes air conditioning, GPS navigation, Bluetooth, reverse camera, and cruise control.', icon: 'car', image: 'img/car_3.png', order: 3, features: ['Air Conditioning', 'GPS Navigation', 'Bluetooth', 'Reverse Camera', 'Cruise Control', 'Sporty Design', 'Premium Interior'] },
          { id: 'toyota-camry', title: 'Toyota Camry', description: 'Reliable mid-size sedan with excellent fuel economy and comfortable ride. The Camry is perfect for business travelers or anyone seeking a smooth, comfortable driving experience. Known for its reliability and low running costs. Features air conditioning, GPS navigation, Bluetooth, reverse camera, and cruise control.', icon: 'car', image: 'img/car_4.png', order: 4, features: ['Air Conditioning', 'GPS Navigation', 'Bluetooth', 'Reverse Camera', 'Cruise Control', 'Spacious Interior', 'Excellent Fuel Economy', 'Reliable'] }
        ] 
      },
      { 
        id: 'services', 
        type: 'services-grid', 
        title: 'Our Services', 
        description: 'Our service is not only renting a car, but we also provide a private chauffeur service that can guide you on your trip and also longtrip packages to support your travel needs.',
        content: 'Our service is not only renting a car, but we also provide a private chauffeur service that can guide you on your trip and also longtrip packages to support your travel needs.',
        order: 4, 
        items: [
          { id: 'airport-service', title: 'Perth Airport Service', description: 'At TAK8, we offer unbeatable deals on airport car rentals in Perth. With transparent pricing and no hidden fees, you get great value for your money. Choose from budget-friendly options, all backed by excellent customer service.', icon: 'airport', image: 'img/service_1.png', link: '/services#airport', order: 1 },
          { id: 'vehicle-rental', title: 'Vehicle Rental Services', description: 'Professional vehicle rental services like TAK8 offer benefits for personal and business needs. With a focus on quality, reliability, and customer satisfaction, TAK8 provides the right vehicle solutions and a seamless rental experience.', icon: 'vehical', image: 'img/service_2.png', link: '/services#vrs', order: 2 },
          { id: 'discounts', title: 'Special Discounts for Extended Rentals', description: 'These special rates are designed to provide exceptional value for customers planning longer stays or extended travel in East Perth and beyond.', icon: 'seats', image: 'img/service_3.png', link: '/services#dis', order: 3 }
        ] 
      },
      { 
        id: 'advantages', 
        type: 'features-grid', 
        title: 'Why Choose Us ?', 
        subtitle: 'ADVANTAGES', 
        description: 'We present many guarantees and advantages when you rent a car with us for your trip. Here are some of the advantages that you will get',
        content: 'We present many guarantees and advantages when you rent a car with us for your trip. Here are some of the advantages that you will get',
        order: 5, 
        items: [
          { id: 'easy-rent', title: 'Easy Rent', description: 'Rent a car at our rental with an easy and fast process without disturbing your productivity', icon: 'rent', order: 1 },
          { id: 'premium-quality', title: 'Premium Quality', description: 'Our cars are always maintained engine health and cleanliness to provide a more comfortable driving experience', icon: 'premium', order: 2 },
          { id: 'professional-agent', title: 'Professional Agent', description: 'You can ask your travel companion to escort and guide your journey.', icon: 'agent', order: 3 },
          { id: 'car-safety', title: 'Car Safety', description: 'We guarantee the safety of the engine on the car always running well with regular checks on the car engine.', icon: 'safety', order: 4 },
          { id: 'refund', title: 'Refund', description: 'Our service guarantee provides a money back opportunity if the car does not match the information provided.', icon: 'refund', order: 5 },
          { id: 'live-monitoring', title: 'Live Monitoring', description: 'Our service provides direct customer monitoring to monitor trips in terms of safety and comfort.', icon: 'live', order: 6 }
        ] 
      },
      { 
        id: 'testimonials', 
        type: 'testimonials', 
        title: 'What Our Customers Say', 
        subtitle: 'TESTIMONIALS', 
        description: 'Here are some comments from our customers, be one of them',
        content: 'Here are some comments from our customers, be one of them',
        order: 6 
      },
      { 
        id: 'footer-info', 
        type: 'footer', 
        title: 'TAK8 Car Rental', 
        description: 'Your trusted car rental partner in Perth, Western Australia. We provide quality vehicles and exceptional service.',
        content: 'TAK8 Car Rental is a locally owned and operated car rental company based in East Perth, Western Australia. We are committed to providing our customers with quality vehicles, competitive prices, and exceptional customer service. Whether you need a car for a day, a week, or longer, we have flexible rental options to suit your needs. Contact us today to book your next rental!',
        order: 7, 
        items: [
          { id: 'quick-links', title: 'Quick Links', description: 'Home - Return to our homepage | About Us - Learn more about TAK8 | Services - View our full range of services | Contact Us - Get in touch with our team | Cars - Browse our complete fleet | Blog - Read our latest news and tips', order: 1 },
          { id: 'office-hours', title: 'Office Hours', description: 'Monday to Friday: 9:00 AM - 5:00 PM | Saturday: By Appointment (9:00 AM - 5:00 PM) | Sunday: By Appointment (9:00 AM - 5:00 PM) | After-hours airport pickups available by arrangement', order: 2 },
          { id: 'head-office', title: 'Head Office', description: '17 Regal Place, East Perth, Western Australia 6004. Our office is conveniently located near the Perth CBD, with easy access from major roads and public transport. Free parking available for customers picking up or returning vehicles.', order: 3 },
          { id: 'contact-info', title: 'Contact Us', description: 'Phone: +61 893 415 334 | Email: info@tak8.com.au | Facebook: TAK8 Australia | Instagram: @tak8_australia | We aim to respond to all inquiries within 24 hours.', order: 4 }
        ] 
      }
    ],
    seo: { metaTitle: 'Perth Car Rental | Hourly & Affordable Car Hire – TAK8', metaDescription: 'TAK8 offers flexible Perth car rental with hourly and long-term options. Enjoy simple booking, no hidden fees, and a wide range of vehicles.', keywords: ['Perth car rental', 'TAK8', 'car hire Perth', 'hourly car rental', 'affordable car hire', 'East Perth car rental', 'Perth Airport car hire', 'cheap car rental Perth'], ogTitle: 'Affordable Perth Car Rental – TAK8', ogDescription: 'Book your Perth car hire with TAK8. Choose hourly or long-term rentals and enjoy transparent pricing with no extra charges.' }
  },
  'about-us': {
    pageSlug: 'about-us', pageTitle: 'About Us',
    sections: [
      { id: 'hero', type: 'hero', title: 'About Us', content: 'Learn more about TAK8 Car Rental - your trusted local partner for car hire in Perth, Western Australia. We are committed to providing quality vehicles and exceptional service.', order: 1 },
      { id: 'about-info', type: 'content', title: 'About TAK8 Car Rental', description: 'Welcome to TAK8 Car Rental, your premier choice for car rentals in East Perth, Western Australia, and beyond. Founded with a commitment to providing unparalleled rental experiences, TAK8 is dedicated to serving the local community and travelers alike with top-notch service, reliability, and convenience.', content: 'TAK8 Car Rental was established with a simple mission: to make car rental in Perth easy, affordable, and enjoyable. As a locally owned and operated business, we understand the unique needs of our customers and are committed to exceeding their expectations. Our team brings years of experience in the car rental industry, combined with a deep knowledge of Perth and Western Australia. Whether you are a tourist exploring the beautiful sights of WA, a business traveler needing reliable transportation, or a local in need of a temporary vehicle, TAK8 is here to serve you with professionalism and care.', order: 2 },
      { id: 'vision', type: 'content', title: 'Our Vision', description: 'At TAK8 Car Rental, our vision is clear: to become the go-to destination for car rentals in East Perth and throughout Western Australia. We aim to set new standards of excellence in the industry by offering a wide selection of vehicles, transparent pricing, and exceptional customer service.', content: 'We envision a future where car rental is seamless, stress-free, and accessible to everyone. Our goal is to be the first choice for car hire in Perth by consistently delivering quality vehicles, competitive prices, and outstanding customer experiences. We are committed to growing our fleet, expanding our services, and continuously improving to meet the evolving needs of our customers. At TAK8, we believe that every journey should be memorable, and we are dedicated to making that happen for each and every customer.', order: 3 },
      { id: 'mission', type: 'content', title: 'Our Mission', description: "TAK8 Car Rental is on a mission to make car rental simple, affordable, and enjoyable. Whether you're exploring the sights of Perth, embarking on a road trip across Western Australia, or in need of temporary transportation, we're here to meet your needs with professionalism and expertise.", content: 'Our mission is built on three core pillars: Quality, Affordability, and Service. We maintain a fleet of well-serviced, clean, and reliable vehicles to ensure your safety and comfort. We offer competitive pricing with no hidden fees, so you always know exactly what you are paying. And we provide friendly, knowledgeable customer service that goes above and beyond to make your rental experience exceptional. From the moment you book to the moment you return your vehicle, we are committed to making your experience with TAK8 the best it can be.', order: 4 },
      { id: 'why-choose', type: 'features-grid', title: 'Why Choose TAK8 Car Rental?', subtitle: 'Why Choose US', description: 'We present many guarantees and advantages when you rent a car with us for your trip. Here are some of the advantages that you will get', content: 'There are many car rental companies in Perth, but TAK8 stands out for our commitment to quality, value, and customer satisfaction. Here is what sets us apart from the competition and why thousands of customers choose TAK8 for their car rental needs.', items: [
        { id: 'local-expertise', title: 'Local Expertise', description: 'As a locally owned and operated business, we understand the unique needs of our customers. Count on us for personalized service, insider tips, and recommendations to make the most of your journey. Our team knows Perth inside and out and can help you plan the perfect trip, whether you are visiting for business or pleasure. We can recommend the best routes, hidden gems, and must-see attractions.', icon: 'local', order: 1 },
        { id: 'quality-fleet', title: 'Quality Fleet', description: 'We take pride in our well-maintained fleet of vehicles, ranging from compact cars to spacious SUVs and 7-seaters. Each vehicle is meticulously inspected and cleaned to ensure your safety and comfort on the road. Our cars undergo regular servicing and safety checks, and we replace vehicles regularly to ensure you always have access to modern, reliable transportation.', icon: 'quality', order: 2 },
        { id: 'convenience', title: 'Convenience', description: 'Located in the heart of WA East Perth, our rental office is easily accessible and conveniently situated for your convenience. Plus, enjoy flexible booking options, extended hours, and hassle-free returns to accommodate your busy schedule. We also offer airport pickup and drop-off services, making it easy to start and end your journey. Our online booking system allows you to reserve your vehicle 24/7.', icon: 'convenience', order: 3 }
      ], order: 5 }
    ],
    seo: { metaTitle: 'About TAK8 | Perth Car Hire Experts', metaDescription: 'TAK8 is your trusted local partner for car rental in Perth. Count on our friendly team, quality vehicles, and a hassle-free booking experience every time.', keywords: ['about TAK8', 'Perth car hire', 'car rental Perth', 'East Perth car rental'], ogTitle: 'Get to Know TAK8 – Perth Car Rental', ogDescription: 'Discover how TAK8 is changing car rental in Perth with flexible, fair, and customer-first service.' }
  },

  'services': {
    pageSlug: 'services', pageTitle: 'Our Services',
    sections: [
      { id: 'hero', type: 'hero', title: 'OUR Services', content: 'Discover the full range of car rental services offered by TAK8. From flexible rental periods to airport pickups and special discounts, we have everything you need for a seamless travel experience in Perth.', order: 1 },
      { id: 'services-intro', type: 'content', title: 'Services Offered by TAK8 Car Rental', subtitle: 'TAK8, Just Make It!', description: 'We present many guarantees and advantages when you rent a car with us for your trip. Here are some of the advantages that you will get', content: 'At TAK8 Car Rental, we offer more than just vehicles - we provide complete transportation solutions tailored to your needs. Whether you need a car for a few hours, a day, a week, or longer, we have flexible options to suit your schedule and budget. Our comprehensive range of services is designed to make your car rental experience as smooth and enjoyable as possible.', order: 2 },
      { id: 'vehicle-options', type: 'alternating', title: 'Vehicle Rental Options', description: 'We take pride in our well-maintained fleet of vehicles, ranging from compact cars to spacious SUVs and 7-seaters. Each vehicle is meticulously inspected and cleaned to ensure your safety and comfort on the road.', content: 'Our diverse fleet includes: Compact Cars - Perfect for city driving and solo travelers, offering excellent fuel economy and easy parking. Sedans - Comfortable mid-size vehicles ideal for business trips or couples. SUVs - Versatile vehicles with extra space for luggage and outdoor adventures. 7 Seaters - Spacious family vehicles perfect for group travel or airport transfers. All vehicles come equipped with modern features including air conditioning, GPS navigation, Bluetooth connectivity, and reverse cameras.', features: ['Compact Cars - Fuel efficient, easy to park, perfect for city driving', 'Sedans - Comfortable, reliable, ideal for business or leisure', 'SUVs - Spacious, versatile, great for adventures', '7 Seaters - Family friendly, plenty of room for passengers and luggage'], order: 3 },
      { id: 'flexible-rental', type: 'alternating', title: 'Flexible Rental Periods', description: 'Choose from a variety of rental periods to suit your needs, whether you need a vehicle for a few hours, a day, a week, two weeks, or longer. Enjoy competitive rates and flexible booking options for hassle-free rentals.', content: 'We understand that every trip is different, which is why we offer flexible rental periods to match your specific needs. Hourly Rentals - Perfect for quick errands or short trips around Perth. Daily Rentals - Ideal for day trips or when you need a car for a full day. Weekly Rentals - Great value for tourists or business travelers staying for a week. Extended Rentals - Special rates for rentals of 14 days or more. Our flexible booking system allows you to extend or shorten your rental period as needed, giving you complete control over your travel plans.', order: 4 },
      { id: 'special-discounts', type: 'alternating', title: 'Special Discounts for Extended Rentals', description: 'These special rates are designed to provide exceptional value for customers planning longer stays or extended travel in East Perth and beyond.', content: 'Save more when you rent longer! We offer special discounted rates for extended rentals: 7-Day Discount - Enjoy a cheaper daily rate when you hire for 7 days or longer. This is perfect for tourists exploring Western Australia or business travelers on week-long assignments. 14-Day Discount - Save even more with our 14-day rate. Ideal for extended holidays, temporary vehicle needs, or long-term business trips. Monthly Rates - Contact us for special monthly rates for rentals of 30 days or more. These discounts are automatically applied when you book, so you always get the best possible price.', features: ['7-Day Hire - Enjoy a cheaper rate for weekly rentals', '14-Day Hire - Even bigger savings for two-week rentals', 'Monthly Rates - Special pricing for long-term rentals', 'No Hidden Fees - The price you see is the price you pay'], order: 5 },
      { id: 'airport-pickup', type: 'alternating', title: 'Airport After Hour Pickup', description: 'Take advantage of our convenient airport after hours pickup available at Perth Airport. Save time and skip the lines by arranging for your rental vehicle to be waiting for you upon arrival.', content: 'We know that flights do not always arrive during business hours, which is why we offer after-hours airport pickup service at Perth Airport. How it works: Book your rental in advance and let us know your flight details. We will arrange for your vehicle to be ready and waiting when you arrive. Our team will meet you at the airport or provide clear instructions for collecting your vehicle. Available 24/7 for arrivals at any time. This service is perfect for late-night arrivals or early-morning flights, ensuring you have transportation ready whenever you land.', order: 6 },
      { id: 'additional-services', type: 'alternating', title: 'Additional Services', description: 'Ensure the safety and comfort of your little ones with our child seat rental options. Never get lost with our optional GPS navigation systems, available for rent with your vehicle. Stay protected on the road with our comprehensive insurance options tailored to your needs.', content: 'We offer a range of additional services to enhance your rental experience: Child Seat Rentals - Keep your little ones safe with our range of child seats suitable for different ages and sizes. Available for infants, toddlers, and older children. GPS Navigation - Never get lost with our portable GPS units. Pre-loaded with Australian maps and points of interest. Insurance Options - Choose from Standard Cover ($0/day, $5000 excess) or Peace of Mind Cover ($22/day, $500 excess) for added protection. Roadside Assistance - 24/7 support in case of breakdowns or emergencies. Additional Drivers - Add extra drivers to your rental for flexibility.', features: ['Child Seat Rentals - Infant, toddler, and booster seats available', 'GPS Navigation Systems - Portable units with Australian maps', 'Comprehensive Insurance Options - Standard and Peace of Mind cover', '24/7 Roadside Assistance - Help is always just a call away', 'Additional Drivers - Add family members or colleagues to your rental'], order: 7 },
      { id: 'corporate-rentals', type: 'alternating', title: 'Corporate and Business Rentals', description: 'We offer special corporate and business rental packages tailored to the unique requirements of your company. Streamline your business travel arrangements with our convenient booking process and dedicated account management services.', content: 'TAK8 offers tailored solutions for businesses of all sizes. Our corporate rental program includes: Dedicated Account Management - A single point of contact for all your rental needs. Priority Booking - Guaranteed vehicle availability for your business trips. Flexible Billing - Monthly invoicing and customized payment terms. Volume Discounts - Special rates based on your rental volume. Fleet Solutions - Long-term vehicle solutions for businesses needing ongoing transportation. Whether you need vehicles for visiting clients, employee travel, or company events, we can create a customized package that meets your business requirements and budget.', order: 8 },
      { id: 'local-expertise', type: 'alternating', title: 'Local Area Expertise', description: 'Our team has extensive knowledge of the Perth area and can provide recommendations for attractions, dining options, and other points of interest. Ask us for insider tips to make the most of your time in Western Australia.', content: 'As locals who love Perth and Western Australia, we are happy to share our knowledge with you. Our team can provide: Driving Route Recommendations - The best routes to popular destinations, scenic drives, and hidden gems. Attraction Suggestions - Must-see sights, beaches, wineries, and natural wonders. Dining Recommendations - From casual cafes to fine dining, we know the best places to eat. Practical Tips - Parking advice, fuel station locations, and local driving customs. Day Trip Ideas - Suggestions for day trips from Perth, including Rottnest Island, Margaret River, and the Pinnacles. Just ask our friendly staff when you pick up your vehicle!', order: 9 },
      { id: 'customer-support', type: 'alternating', title: 'Customer Support', description: 'Our friendly and knowledgeable customer support team is available to assist you with any questions or concerns before, during, and after your rental period. Contact us via phone, email, or visit our office in East Perth for personalized assistance.', content: 'At TAK8, customer satisfaction is our top priority. Our support team is here to help you every step of the way: Before Your Rental - Help with booking, vehicle selection, and answering any questions. During Your Rental - 24/7 support for emergencies, breakdowns, or any issues that arise. After Your Rental - Assistance with invoices, feedback, and future bookings. Contact us by: Phone: +61 893 415 334 | Email: info@tak8.com.au | In Person: 17 Regal Place, East Perth. We aim to respond to all inquiries within 24 hours and resolve any issues promptly and professionally.', order: 10 }
    ],
    seo: { metaTitle: 'Flexible Car Hire Perth | Hourly & Long-Term Rental – TAK8', metaDescription: 'Hire a car in Perth by the hour, day, or week. Enjoy clear, affordable pricing and a hassle-free booking experience with TAK8.', keywords: ['car hire Perth', 'Perth car rental', 'hourly car hire', 'long-term rental', 'airport pickup Perth', 'corporate car rental'], ogTitle: 'Hourly & Long-Term Car Hire – TAK8 Perth', ogDescription: 'TAK8 makes car hire in Perth easy and flexible. Choose from hourly, daily, or long-term rentals to fit your trip.' }
  },
  'contact-us': {
    pageSlug: 'contact-us', pageTitle: 'Contact Us',
    sections: [
      { id: 'hero', type: 'hero', title: 'Contact Us', content: 'Get in touch with TAK8 Car Rental. We are here to help with your car rental needs, answer your questions, and provide personalized assistance.', order: 1 },
      { id: 'contact-info', type: 'contact-info', title: 'GET IN TOUCH', description: "For customized rental solutions, please email us or give us a call—we're happy to assist you!", content: 'We would love to hear from you! Whether you have questions about our vehicles, need help with a booking, or want to discuss a customized rental solution, our friendly team is here to help. Contact us using any of the methods below, and we will get back to you as soon as possible. Phone: +61 893 415 334 - Call us during business hours for immediate assistance. Email: info@tak8.com.au - Send us an email anytime, and we will respond within 24 hours. Address: 17 Regal Place, East Perth - Visit our office for in-person assistance. Business Hours: Monday to Friday 9:00 AM - 5:00 PM, Weekends by appointment. Follow us on social media: Facebook - TAK8 Australia, Instagram - @tak8_australia', order: 2 }
    ],
    seo: { metaTitle: 'Contact TAK8 | Perth Car Rental Support', metaDescription: 'Need help with your Perth car rental? Contact TAK8 for fast support, quotes, and friendly service.', keywords: ['contact TAK8', 'Perth car rental support', 'car hire help', 'TAK8 phone number', 'TAK8 email'], ogTitle: 'Talk to TAK8 – Perth Car Hire Help', ogDescription: 'Reach out to our Perth team for quick answers, quotes, or friendly support' }
  },
  'privacy-policy': {
    pageSlug: 'privacy-policy', pageTitle: 'Privacy Policy',
    sections: [
      { id: 'hero', type: 'hero', title: 'TAK8 Car Rental Privacy Policy', order: 1 },
      { id: 'introduction', type: 'content', title: 'Introduction', description: 'TAK8 Car Rental ("we," "our," "us") respects your privacy and is committed to protecting your personal data. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website (https://tak8.com.au/) and use our services. Please read this policy carefully to understand our views and practices regarding your personal data and how we will treat it.', order: 2 },
      { id: 'personal-info', type: 'content', title: 'Personal Information We Collect', description: 'When you visit the Site or make a booking request, we collect certain information about you and your device. This may include: Personal Identification Information (Name, address, email address, phone number, date of birth, driver\'s license details, and payment information), Device Information (Information about your web browser, IP address, time zone, and some of the cookies installed on your device), Browsing Information (Information about the individual web pages or products that you view, what websites or search terms referred you to the Site, and information about how you interact with the Site), Location Data (GPS data from rented vehicles for tracking and safety purposes), Communication Data (Records of your communications with us, including emails, phone calls, and messages).', order: 3 },
      { id: 'how-we-use', type: 'content', title: 'How Do We Use Your Personal Information?', description: 'We use the information we collect in the following ways: To Provide Services (Process your rental bookings, manage your rental agreements, and provide customer support), To Improve Our Services (Analyze usage trends, improve our website and services, and develop new features), For Marketing (Send you promotional materials, newsletters, and other information about our services - you can opt out at any time), For Safety and Security (Ensure the safety and security of our customers, employees, and property, including tracking rented vehicles), Legal Compliance (Comply with legal obligations, resolve disputes, and enforce our agreements).', order: 4 },
      { id: 'sharing', type: 'content', title: 'Sharing Your Personal Information', description: 'We may share your personal information with third parties in the following circumstances: Service Providers (Third-party vendors who perform services on our behalf, such as payment processing, customer support, and marketing), Business Partners (Partners with whom we collaborate to provide services, including insurance companies and roadside assistance providers), Legal Authorities (Government authorities and law enforcement agencies when required by law or to protect our rights and interests), Affiliates (Our subsidiaries and affiliated companies for business purposes).', order: 5 },
      { id: 'advertising', type: 'content', title: 'Behavioural Advertising', description: 'We use your personal information to provide you with targeted advertisements or marketing communications we believe may be of interest to you. You can opt out of targeted advertising through Facebook, Google, Bing, or by visiting the Digital Advertising Alliance\'s opt-out portal at http://optout.aboutads.info/.', order: 6 },
      { id: 'do-not-track', type: 'content', title: 'Do Not Track', description: "Please note that we do not alter our Site's data collection and use practices when we see a Do Not Track signal from your browser.", order: 7 },
      { id: 'your-rights', type: 'content', title: 'Your Rights', description: 'If you are a European resident, you have the right to access personal information we hold about you and to ask that your personal information be corrected, updated, or deleted. Additionally, if you are a European resident, we note that we are processing your information to fulfill contracts we might have with you, or otherwise to pursue our legitimate business interests listed above.', order: 8 },
      { id: 'data-retention', type: 'content', title: 'Data Retention', description: 'When you place a request through the Site, we will maintain your Form Information for our records unless and until you ask us to delete this information.', order: 9 },
      { id: 'changes', type: 'content', title: 'Changes', description: 'We may update this privacy policy from time to time in order to reflect, for example, changes to our practices or for other operational, legal, or regulatory reasons.', order: 10 },
      { id: 'contact', type: 'content', title: 'Contact Us', description: 'For more information about our privacy practices, if you have questions, or if you would like to make a complaint, please contact us by e-mail at info@tak8.com.au or by mail at TAK8 Car Rental, 17 Regal Place East Perth.', order: 11 }
    ],
    seo: { metaTitle: 'Privacy Policy | TAK8 Car Rental', metaDescription: 'Read TAK8 Car Rental privacy policy. Learn how we collect, use, and protect your personal information when using our car rental services in Perth.', keywords: ['privacy policy', 'TAK8 privacy', 'data protection', 'personal information'], ogTitle: 'Privacy Policy | TAK8', ogDescription: 'How we protect your information at TAK8 Car Rental.' }
  },
  'terms-and-conditions': {
    pageSlug: 'terms-and-conditions', pageTitle: 'Terms & Conditions',
    sections: [
      { id: 'hero', type: 'hero', title: 'TAK8 CAR RENTAL – TERMS AND CONDITIONS', order: 1 },
      { id: 'intro', type: 'content', title: '', description: 'These terms and conditions are supplementary to, and does not override, any rights or obligations under the Competition and Consumer Act or similar State Laws. References to "the vehicle" refer to the rental car covered by this agreement.', order: 2 },
      { id: 'responsibilities', type: 'content', title: '1. Responsibilities of the Renter', description: 'Return: The vehicle must be returned to the TAK8 depot/yard from where it was collected at the agreed time. Substance-Free Driving: Driving under the influence of alcohol or drugs is prohibited. License Validity: The renter and any additional drivers must have valid driver\'s licenses. Accountability: The renter is responsible for any breaches of these terms by additional drivers. Authorized Drivers Only: No unauthorized individuals are allowed to operate the vehicle. Road Conditions: The vehicle is to be driven only on paved roads. Water Hazards: Avoid driving through water bodies such as rivers, creeks, or flooded roads. No Roof Items: Do not carry surfboards or other items on the roof of the vehicle. Replacement Key Fee: If the rental car key is lost during your rental period, the renter will be responsible for covering the full replacement cost, including any associated fees. Fuel: Return the vehicle with a full tank of unleaded petrol; shortages will be charged at $3.00 per litre. Cleanliness: Return the vehicle in a clean condition, or a $75 cleaning fee applies. No Smoking or Pets: Smoking, vaping, or having pets in the vehicle incurs a $200 cleaning fee. Traffic Infringements: A $25 administrative fee applies for each traffic infringement in addition to the fine. Fair Use Policy: The vehicle is not to be used for security patrols or courier services. Geographic Limits: Operate the vehicle within 500km of Perth; boundaries include Geraldton, Paynes Find, Southern Cross, Jerramungup, Albany. Extended Use Area: A $150 fee applies to extend the area of use; extended boundaries include Exmouth, Carnarvon, Monkey Mia, Kalbarri, Meekatharra, Kalgoorlie, Norseman, Esperance.', order: 3 },
      { id: 'insurance', type: 'content', title: '2. Insurance Options and Damage Liability', description: 'TAK8 offers two levels of coverage so you can choose the level of protection that suits your needs.', order: 4 },
      { id: 'standard-cover', type: 'content', title: 'STANDARD COVER – Collision Damage Responsibility', description: 'Cost: $0.00 per day. Single Vehicle Excess: $5000 (Regardless of fault). With this option, your maximum liability in the event of any accident or damage — including single vehicle damage — is $5000.', order: 5 },
      { id: 'peace-of-mind', type: 'content', title: 'PEACE OF MIND COVER – Reduced Liability Protection', description: 'Cost: $22.00 per day. Single Vehicle Excess: $500 (Regardless of fault). Security Bond: $200. The maximum liability is reduced to $500 under the PEACE OF MIND COVER. For single vehicle incidents this also includes minor damages such as: Scratches to the vehicle\'s paintwork, Minor dents or dings on body panels, Curb rash or scuff marks on alloy wheels, Cracked or chipped side mirrors, headlights or taillights, Bent or cracked number plates, Damage to front or rear bumpers from low-speed impacts, Broken or missing wiper blades, Damage to plastic trim or mouldings, Cracks or chips in the windshield, Door edge dents, Underbody scrapes, Minor scuffs on side skirts or bumpers, Punctured or flat tires, Misalignment or damage to sensors.', order: 6 },
      { id: 'single-vehicle-excess', type: 'content', title: "What is 'Single Vehicle Excess'?", description: 'Single Vehicle Excess is the maximum amount you must pay if the rental vehicle is damaged in an incident where no other vehicle is involved. This includes: Collisions with stationary objects (e.g., poles, barriers, trees), Damage from scraping curbs or reversing into walls, Driving off-road or into potholes causing suspension or underbody damage, Any damage where no third-party vehicle is part of the accident. Under STANDARD COVER, the excess is $5000. Under PEACE OF MIND COVER, the excess is reduced to $500.', order: 7 },
      { id: 'excluded-damages', type: 'content', title: 'Excluded Damages (Not Covered by Any Waiver)', description: 'Failure to immediately report an accident or incident (within 6–12 hours at most) to TAK8, or fleeing the scene. Intentional or reckless damage, including driving under the influence of drugs or alcohol. Unauthorized use, including driving outside the approved geographical area or by an unauthorized driver. Interior damage such as stains, rips, burns, or damage from negligence. Mechanical damage caused by ignoring dashboard warnings. Towing the vehicle without prior authorization from TAK8. Leaving the vehicle unlocked or leaving the keys inside the vehicle. Underbody or water damage, regardless of cause. Theft or loss resulting from failure to return the keys or secure the vehicle. Damage incurred during a breach of this agreement.', order: 8 },
      { id: 'inspections', type: 'content', title: '3. Pre-Rental and Post-Rental Inspections', description: "All vehicles are inspected before and after each rental. Renters are encouraged to review the vehicle's condition with a TAK8 representative at pick-up and note any existing damage to avoid disputes upon return.", order: 9 },
      { id: 'business-hours', type: 'content', title: '4. Business Hours', description: 'Monday to Friday: 9:00 AM to 5:00 PM. Saturday & Sunday: Available by booking between 9:00 AM to 5:00 PM.', order: 10 },
      { id: 'non-return', type: 'content', title: 'Non-Return, Non-Payment & Rental Extensions', description: 'If the vehicle is not returned on the agreed date and/or rental payment is not made in full, all insurance cover (including PEACE OF MIND COVER) becomes void. The renter is then fully liable for the full standard excess of $5,000 and any associated recovery or repair costs. Extensions requested by phone require the renter to bring the vehicle to the TAK8 depot for inspection and provide clear photos of the vehicle\'s current condition before the rental period and insurance cover can be extended. Insurance cover will only apply if the rental account is fully up to date. Payments made in arrears to bring the account current do not provide retrospective insurance cover for the overdue period.', order: 11 },
      { id: 'tracking', type: 'content', title: '5. Vehicle Tracking and Recovery', description: 'TAK8 vehicles may be equipped with tracking devices for security and recovery purposes. If a vehicle is not returned on time or is believed to be stolen, TAK8 reserves the right to track, disable, and recover the vehicle. Personal details may be shared with authorities in the event of theft or a serious breach of this agreement.', order: 12 }
    ],
    seo: { metaTitle: 'Terms and Conditions | TAK8 Car Rental', metaDescription: 'Read the terms and conditions for TAK8 Car Rental services in Perth. Understand your responsibilities, insurance options, and rental policies.', keywords: ['terms and conditions', 'TAK8 terms', 'car rental terms', 'rental agreement'], ogTitle: 'Terms and Conditions | TAK8', ogDescription: 'Rental terms and conditions for TAK8 Car Rental Perth.' }
  }
};

const WEBSITE_CONTACT_INFO: WebsiteContactInfo[] = [
  { type: 'phone', label: 'Phone', value: '+61 893 415 334', icon: 'call', order: 1, active: true },
  { type: 'email', label: 'Email', value: 'info@tak8.com.au', icon: 'mail', order: 2, active: true },
  { type: 'address', label: 'Address', value: '17 Regal Place East Perth', icon: 'pin', order: 3, active: true },
  { type: 'hours', label: 'Business Hours', value: 'Mon - Fri: 9:00 AM - 5:00 PM', icon: 'date', order: 4, active: true },
  { type: 'social', label: 'Facebook', value: 'https://www.facebook.com/people/TAK8/', icon: 'facebook', order: 5, active: true },
  { type: 'social', label: 'Instagram', value: 'https://www.instagram.com/tak8_australia/', icon: 'instagram', order: 6, active: true }
];

const getWebsiteContent = (pageSlug: string): WebsitePageContent | null => WEBSITE_CONTENT[pageSlug] || null;

const AVAILABLE_ICONS = [
  { name: 'agent', label: 'Agent' }, { name: 'airport', label: 'Airport' }, { name: 'call', label: 'Call' },
  { name: 'car', label: 'Car' }, { name: 'check', label: 'Check' }, { name: 'clock', label: 'Clock' },
  { name: 'convenience', label: 'Convenience' }, { name: 'date', label: 'Date' }, { name: 'email', label: 'Email' },
  { name: 'live', label: 'Live' }, { name: 'local', label: 'Local' }, { name: 'location', label: 'Location' },
  { name: 'mail', label: 'Mail' }, { name: 'mission', label: 'Mission' }, { name: 'pin', label: 'Pin' },
  { name: 'premium', label: 'Premium' }, { name: 'quality', label: 'Quality' }, { name: 'refund', label: 'Refund' },
  { name: 'rent', label: 'Rent' }, { name: 'safety', label: 'Safety' }, { name: 'seats', label: 'Seats' },
  { name: 'vehical', label: 'Vehicle' }, { name: 'vision', label: 'Vision' },
];


// Page configurations
const WEBSITE_PAGES = [
  { slug: 'home', title: 'Home Page', icon: HomeIcon, description: 'Main landing page with hero, services, and advantages', url: 'https://tak8.com.au/', color: 'blue', priority: 1 },
  { slug: 'about-us', title: 'About Us', icon: InformationCircleIcon, description: 'Company information, vision, and mission', url: 'https://tak8.com.au/about-us', color: 'purple', priority: 2 },
  { slug: 'services', title: 'Services', icon: WrenchScrewdriverIcon, description: 'All services - rentals, airport pickup, discounts', url: 'https://tak8.com.au/services', color: 'green', priority: 3 },
  { slug: 'contact-us', title: 'Contact Us', icon: PhoneIcon, description: 'Contact information and business hours', url: 'https://tak8.com.au/contact-us', color: 'orange', priority: 4 },
  { slug: 'privacy-policy', title: 'Privacy Policy', icon: DocumentTextIcon, description: 'Privacy policy and data handling', url: 'https://tak8.com.au/privacy-policy', color: 'slate', priority: 5 },
  { slug: 'terms-and-conditions', title: 'Terms & Conditions', icon: DocumentTextIcon, description: 'Terms of service and rental conditions', url: 'https://tak8.com.au/terms-conditions', color: 'slate', priority: 6 },
];

type TabType = 'pages' | 'contact' | 'settings';

// Main Component
const WebsiteCMS: React.FC = () => {
  const [pages, setPages] = useState<PageContent[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabType>('pages');
  const [selectedPage, setSelectedPage] = useState<string | null>(() => {
    // Restore selected page from sessionStorage on mount
    return sessionStorage.getItem('cms_selected_page') || null;
  });
  const [editingPage, setEditingPage] = useState<PageContent | null>(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' | 'warning' } | null>(null);
  const [showHelp, setShowHelp] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  const showToast = useCallback((message: string, type: 'success' | 'error' | 'info' | 'warning' = 'info') => {
    setToast({ message, type });
  }, []);

  const fetchPages = async () => {
    try {
      setLoading(true);
      const res = await pageService.getAllPages(true);
      const anyRes = res as any;
      const list: PageContent[] = (anyRes?.data?.data?.pages as PageContent[]) || (anyRes?.data?.pages as PageContent[]) || (anyRes?.pages as PageContent[]) || (Array.isArray(anyRes) ? anyRes : []);
      setPages(list);
      return list;
    } catch (e) {
      console.error('Failed to load pages:', e);
      setPages([]);
      return [];
    } finally {
      setLoading(false);
    }
  };

  // Persist selectedPage to sessionStorage
  useEffect(() => {
    if (selectedPage) {
      sessionStorage.setItem('cms_selected_page', selectedPage);
    } else {
      sessionStorage.removeItem('cms_selected_page');
    }
  }, [selectedPage]);

  // On mount, fetch pages and restore editing state if there was a selected page
  useEffect(() => {
    const initializeEditor = async () => {
      const fetchedPages = await fetchPages();
      const savedSelectedPage = sessionStorage.getItem('cms_selected_page');
      
      if (savedSelectedPage && fetchedPages.length > 0) {
        // Restore the editing page from fetched data
        const pageData = fetchedPages.find(p => p.pageSlug === savedSelectedPage);
        if (pageData) {
          setEditingPage(pageData);
          setSelectedPage(savedSelectedPage);
        } else {
          // Page not in database, create new from template
          const pageConfig = WEBSITE_PAGES.find(p => p.slug === savedSelectedPage);
          if (pageConfig) {
            const newPage: Partial<PageContent> = {
              pageSlug: savedSelectedPage, 
              pageTitle: pageConfig.title || savedSelectedPage, 
              sections: [],
              seo: { metaTitle: pageConfig.title || '', metaDescription: pageConfig.description || '', keywords: [] },
              status: 'published', // Default to published so changes appear on website immediately
              version: 1,
            };
            setEditingPage(newPage as PageContent);
          } else {
            // Invalid saved page, clear it
            sessionStorage.removeItem('cms_selected_page');
            setSelectedPage(null);
          }
        }
      }
    };
    
    initializeEditor();
  }, []);

  const getPageData = (slug: string): PageContent | undefined => pages.find(p => p.pageSlug === slug);

  const handleEditPage = (slug: string) => {
    const pageData = getPageData(slug);
    if (pageData) {
      setEditingPage(pageData);
      setSelectedPage(slug);
    } else {
      const pageConfig = WEBSITE_PAGES.find(p => p.slug === slug);
      const newPage: Partial<PageContent> = {
        pageSlug: slug, pageTitle: pageConfig?.title || slug, sections: [],
        seo: { metaTitle: pageConfig?.title || '', metaDescription: pageConfig?.description || '', keywords: [] },
        status: 'published', version: 1, // Default to published so changes appear on website immediately
      };
      setEditingPage(newPage as PageContent);
      setSelectedPage(slug);
    }
  };

  const handleSavePage = async () => {
    if (!editingPage) return;
    try {
      setSaving(true);
      let savedPage;
      if (editingPage._id) { 
        savedPage = await pageService.updatePageContent(editingPage._id, editingPage); 
      } else { 
        savedPage = await pageService.createPageContent(editingPage); 
      }
      showToast('Page saved successfully! Changes are now live.', 'success');
      await fetchPages();
      // Update the editing page with the saved data (including _id if it was a new page)
      if (savedPage) {
        setEditingPage(savedPage);
      }
      // Stay on the editor - don't reset selectedPage or editingPage
    } catch (error) {
      console.error('Error saving page:', error);
      showToast('Failed to save page. Please try again.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => { 
    sessionStorage.removeItem('cms_selected_page');
    setSelectedPage(null); 
    setEditingPage(null); 
  };

  const updateEditingPage = (field: string, value: any) => {
    if (!editingPage) return;
    setEditingPage({ ...editingPage, [field]: value });
  };

  const updateSEO = (field: string, value: any) => {
    if (!editingPage) return;
    setEditingPage({ ...editingPage, seo: { ...editingPage.seo, [field]: value } });
  };

  const loadContent = (content: { pageTitle: string; sections: any[]; seo: any }) => {
    if (!editingPage) return;
    setEditingPage({ ...editingPage, pageTitle: content.pageTitle, sections: content.sections, seo: { ...editingPage.seo, ...content.seo } });
  };

  const handleResetToHardcoded = async () => {
    if (!editingPage?._id) return;
    try {
      await pageService.deletePageContent(editingPage._id);
      showToast('Page reset to default content.', 'success');
      await fetchPages();
      setSelectedPage(null);
      setEditingPage(null);
    } catch (error) {
      console.error('Error resetting page:', error);
      showToast('Failed to reset page.', 'error');
    }
  };

  // Render page editor
  if (selectedPage && editingPage) {
    return (
      <>
        {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
        <PageEditor page={editingPage} pageConfig={WEBSITE_PAGES.find(p => p.slug === selectedPage)}
          onSave={handleSavePage} onCancel={handleCancelEdit} onChange={updateEditingPage} onSEOChange={updateSEO}
          onLoadContent={loadContent} onResetToHardcoded={handleResetToHardcoded} saving={saving} showToast={showToast} />
      </>
    );
  }

  const configuredCount = WEBSITE_PAGES.filter(p => getPageData(p.slug)).length;
  const publishedCount = WEBSITE_PAGES.filter(p => getPageData(p.slug)?.status === 'published').length;
  const completionPercent = Math.round((publishedCount / WEBSITE_PAGES.length) * 100);

  const filteredPages = WEBSITE_PAGES.filter(p => 
    p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.description.toLowerCase().includes(searchQuery.toLowerCase())
  );


  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      
      {/* Animated Background Pattern */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none opacity-30">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-400 rounded-full mix-blend-multiply filter blur-3xl animate-blob" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-400 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-2000" />
        <div className="absolute top-1/2 left-1/2 w-80 h-80 bg-pink-400 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-4000" />
      </div>

      <div className="relative p-6 space-y-6">
        {/* Hero Header */}
        <div className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-xl border border-white/50 overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-700 px-8 py-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              <div className="flex items-center gap-5">
                <div className="p-4 bg-white/20 backdrop-blur rounded-2xl shadow-lg">
                  <GlobeAltIcon className="h-10 w-10 text-white" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-white tracking-tight">Website Content Manager</h1>
                  <p className="text-blue-100 mt-1 text-lg">Manage all your website pages from one powerful dashboard</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button onClick={() => setShowHelp(!showHelp)}
                  className="inline-flex items-center px-5 py-3 bg-white/20 backdrop-blur text-white rounded-xl hover:bg-white/30 transition-all font-medium">
                  <QuestionMarkCircleIcon className="h-5 w-5 mr-2" />
                  Quick Guide
                </button>
                <a href="https://tak8.com.au" target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center px-5 py-3 bg-white text-blue-700 rounded-xl hover:bg-blue-50 font-semibold shadow-lg hover:shadow-xl transition-all">
                  <ArrowTopRightOnSquareIcon className="h-5 w-5 mr-2" />
                  View Live Site
                </a>
              </div>
            </div>
          </div>

          {/* Stats Dashboard */}
          <div className="px-8 py-6 bg-gradient-to-r from-slate-50 to-blue-50 border-t border-blue-100">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Total Pages</p>
                    <p className="text-3xl font-bold text-gray-900 mt-1">{WEBSITE_PAGES.length}</p>
                  </div>
                  <div className="p-3 bg-blue-100 rounded-xl"><BookOpenIcon className="h-6 w-6 text-blue-600" /></div>
                </div>
              </div>
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Configured</p>
                    <p className="text-3xl font-bold text-blue-600 mt-1">{configuredCount}</p>
                  </div>
                  <div className="p-3 bg-blue-100 rounded-xl"><AdjustmentsHorizontalIcon className="h-6 w-6 text-blue-600" /></div>
                </div>
              </div>
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Published</p>
                    <p className="text-3xl font-bold text-green-600 mt-1">{publishedCount}</p>
                  </div>
                  <div className="p-3 bg-green-100 rounded-xl"><RocketLaunchIcon className="h-6 w-6 text-green-600" /></div>
                </div>
              </div>
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Completion</p>
                    <p className="text-3xl font-bold text-indigo-600 mt-1">{completionPercent}%</p>
                  </div>
                  <div className="p-3 bg-indigo-100 rounded-xl"><ChartBarIcon className="h-6 w-6 text-indigo-600" /></div>
                </div>
                <ProgressBar progress={completionPercent} />
              </div>
            </div>
          </div>
        </div>

        {/* Help Panel */}
        {showHelp && (
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl border-2 border-amber-200 p-6 shadow-lg animate-fade-in">
            <div className="flex items-start gap-5">
              <div className="p-3 bg-amber-100 rounded-xl"><LightBulbIcon className="h-8 w-8 text-amber-600" /></div>
              <div className="flex-1">
                <h3 className="font-bold text-amber-900 text-xl mb-4">🚀 Quick Start Guide</h3>
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="bg-white/60 rounded-xl p-4 border border-amber-200">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="w-7 h-7 bg-amber-500 text-white rounded-full flex items-center justify-center text-sm font-bold">1</span>
                      <span className="font-semibold text-amber-900">Select Page</span>
                    </div>
                    <p className="text-sm text-amber-800">Click "Edit Content" on any page card to start editing</p>
                  </div>
                  <div className="bg-white/60 rounded-xl p-4 border border-amber-200">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="w-7 h-7 bg-amber-500 text-white rounded-full flex items-center justify-center text-sm font-bold">2</span>
                      <span className="font-semibold text-amber-900">Load Content</span>
                    </div>
                    <p className="text-sm text-amber-800">Click "Load from Website" to import existing content instantly</p>
                  </div>
                  <div className="bg-white/60 rounded-xl p-4 border border-amber-200">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="w-7 h-7 bg-amber-500 text-white rounded-full flex items-center justify-center text-sm font-bold">3</span>
                      <span className="font-semibold text-amber-900">Publish</span>
                    </div>
                    <p className="text-sm text-amber-800">Set status to "Published" and save to make changes live</p>
                  </div>
                </div>
              </div>
              <button onClick={() => setShowHelp(false)} className="p-2 hover:bg-amber-200 rounded-lg transition-colors">
                <XMarkIcon className="h-5 w-5 text-amber-600" />
              </button>
            </div>
          </div>
        )}


        {/* Main Content */}
        <div className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-xl border border-white/50 overflow-hidden">
          {/* Tabs & Search */}
          <div className="border-b border-gray-200 bg-gradient-to-r from-gray-50 to-slate-50">
            <div className="flex items-center justify-between px-6 py-4">
              <nav className="flex gap-1">
                {[
                  { id: 'pages', label: 'Website Pages', icon: BookOpenIcon, count: WEBSITE_PAGES.length },
                  { id: 'contact', label: 'Contact Info', icon: PhoneIcon },
                  { id: 'settings', label: 'Site Settings', icon: WrenchScrewdriverIcon },
                ].map((tab) => (
                  <button key={tab.id} onClick={() => setActiveTab(tab.id as TabType)}
                    className={`flex items-center gap-2 py-3 px-5 rounded-xl font-medium text-sm transition-all ${
                      activeTab === tab.id
                        ? 'bg-white text-blue-600 shadow-md border border-blue-100'
                        : 'text-gray-500 hover:text-gray-700 hover:bg-white/50'
                    }`}>
                    <tab.icon className="h-4 w-4" />
                    {tab.label}
                    {tab.count && (
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                        activeTab === tab.id ? 'bg-blue-100 text-blue-600' : 'bg-gray-200 text-gray-600'
                      }`}>{tab.count}</span>
                    )}
                  </button>
                ))}
              </nav>
              
              {activeTab === 'pages' && (
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input type="text" placeholder="Search pages..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 pr-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-64" />
                  </div>
                  <div className="flex bg-gray-100 rounded-lg p-1">
                    <button onClick={() => setViewMode('grid')}
                      className={`p-2 rounded-lg transition-colors ${viewMode === 'grid' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'}`}>
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                      </svg>
                    </button>
                    <button onClick={() => setViewMode('list')}
                      className={`p-2 rounded-lg transition-colors ${viewMode === 'list' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'}`}>
                      <Bars3Icon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="p-6">
            {activeTab === 'pages' && (
              <PagesTab pages={pages} loading={loading} onEditPage={handleEditPage} getPageData={getPageData}
                filteredPages={filteredPages} viewMode={viewMode} />
            )}
            {activeTab === 'contact' && <ContactInfoTab showToast={showToast} />}
            {activeTab === 'settings' && <SiteSettingsTab showToast={showToast} />}
          </div>
        </div>
      </div>

      {/* CSS for animations */}
      <style>{`
        @keyframes blob { 0%, 100% { transform: translate(0, 0) scale(1); } 33% { transform: translate(30px, -50px) scale(1.1); } 66% { transform: translate(-20px, 20px) scale(0.9); } }
        .animate-blob { animation: blob 7s infinite; }
        .animation-delay-2000 { animation-delay: 2s; }
        .animation-delay-4000 { animation-delay: 4s; }
        @keyframes slide-up { from { transform: translateY(100px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        .animate-slide-up { animation: slide-up 0.3s ease-out; }
        @keyframes fade-in { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fade-in { animation: fade-in 0.3s ease-out; }
      `}</style>
    </div>
  );
};


// Pages Tab Component
interface PagesTabProps {
  pages: PageContent[];
  loading: boolean;
  onEditPage: (slug: string) => void;
  getPageData: (slug: string) => PageContent | undefined;
  filteredPages: typeof WEBSITE_PAGES;
  viewMode: 'grid' | 'list';
}

const PagesTab: React.FC<PagesTabProps> = ({ pages, loading, onEditPage, getPageData, filteredPages, viewMode }) => {
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="relative">
          <div className="w-16 h-16 border-4 border-blue-200 rounded-full animate-spin border-t-blue-600" />
          <GlobeAltIcon className="h-8 w-8 text-blue-600 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
        </div>
        <p className="mt-6 text-gray-600 font-medium text-lg">Loading your pages...</p>
      </div>
    );
  }

  const getColorClasses = (color: string, isConfigured: boolean) => {
    if (!isConfigured) return { bg: 'bg-gray-100', icon: 'text-gray-400', border: 'border-gray-200', gradient: 'from-gray-50 to-gray-100' };
    const colors: Record<string, any> = {
      blue: { bg: 'bg-blue-100', icon: 'text-blue-600', border: 'border-blue-200', gradient: 'from-blue-50 to-blue-100' },
      purple: { bg: 'bg-purple-100', icon: 'text-purple-600', border: 'border-purple-200', gradient: 'from-purple-50 to-purple-100' },
      green: { bg: 'bg-green-100', icon: 'text-green-600', border: 'border-green-200', gradient: 'from-green-50 to-green-100' },
      orange: { bg: 'bg-orange-100', icon: 'text-orange-600', border: 'border-orange-200', gradient: 'from-orange-50 to-orange-100' },
      slate: { bg: 'bg-slate-100', icon: 'text-slate-600', border: 'border-slate-200', gradient: 'from-slate-50 to-slate-100' },
    };
    return colors[color] || colors.blue;
  };

  if (filteredPages.length === 0) {
    return (
      <div className="text-center py-16">
        <MagnifyingGlassIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
        <p className="text-gray-500 text-lg">No pages found matching your search</p>
      </div>
    );
  }

  if (viewMode === 'list') {
    return (
      <div className="space-y-3">
        {filteredPages.map((pageConfig) => {
          const pageData = getPageData(pageConfig.slug);
          const Icon = pageConfig.icon;
          const isConfigured = !!pageData;
          const isPublished = pageData?.status === 'published';
          const colors = getColorClasses(pageConfig.color, isConfigured);
          const sectionCount = pageData?.sections?.length || 0;

          return (
            <div key={pageConfig.slug}
              className={`bg-white border-2 ${isConfigured ? colors.border : 'border-gray-200'} rounded-2xl p-4 hover:shadow-lg transition-all group flex items-center justify-between`}>
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-xl ${colors.bg} group-hover:scale-110 transition-transform`}>
                  <Icon className={`h-6 w-6 ${colors.icon}`} />
                </div>
                <div>
                  <div className="flex items-center gap-3">
                    <h3 className="font-bold text-gray-900">{pageConfig.title}</h3>
                    <span className="text-xs text-gray-400 font-mono">/{pageConfig.slug}</span>
                    {isConfigured && (
                      <span className="text-xs text-gray-500 flex items-center gap-1">
                        <DocumentTextIcon className="h-3 w-3" />{sectionCount} sections
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 mt-0.5">{pageConfig.description}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {isConfigured ? (
                  isPublished ? (
                    <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                      <CheckCircleSolid className="h-4 w-4 mr-1" />Live
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">Draft</span>
                  )
                ) : (
                  <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-500">Not Set</span>
                )}
                <button onClick={() => onEditPage(pageConfig.slug)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 font-medium text-sm transition-colors">
                  {isConfigured ? 'Edit' : 'Setup'}
                </button>
                <a href={pageConfig.url} target="_blank" rel="noopener noreferrer"
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                  <ArrowTopRightOnSquareIcon className="h-5 w-5" />
                </a>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Quick Action Banner */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-5 flex items-center justify-between text-white shadow-lg">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-white/20 rounded-xl"><SparklesIcon className="h-6 w-6" /></div>
          <div>
            <p className="font-semibold text-lg">Quick Start</p>
            <p className="text-blue-100 text-sm">Click any page below, then use "Load from Website" to import content instantly!</p>
          </div>
        </div>
        <button onClick={() => onEditPage('home')} className="px-5 py-2.5 bg-white text-blue-600 rounded-xl font-semibold hover:bg-blue-50 transition-colors shadow-md">
          Start with Home Page →
        </button>
      </div>

      {/* Pages Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {filteredPages.map((pageConfig, index) => {
          const pageData = getPageData(pageConfig.slug);
          const Icon = pageConfig.icon;
          const isConfigured = !!pageData;
          const isPublished = pageData?.status === 'published';
          const colors = getColorClasses(pageConfig.color, isConfigured);
          const sectionCount = pageData?.sections?.length || 0;

          return (
            <div key={pageConfig.slug}
              className={`bg-gradient-to-br ${colors.gradient} border-2 ${isConfigured ? colors.border : 'border-gray-200'} rounded-2xl p-6 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 group relative overflow-hidden`}
              style={{ animationDelay: `${index * 50}ms` }}>
              
              {/* Priority Badge */}
              {pageConfig.priority === 1 && (
                <div className="absolute top-3 right-3">
                  <span className="flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-semibold">
                    <StarSolid className="h-3 w-3" />Primary
                  </span>
                </div>
              )}

              <div className="flex items-start gap-4 mb-4">
                <div className={`p-4 rounded-2xl ${colors.bg} group-hover:scale-110 transition-transform shadow-sm`}>
                  <Icon className={`h-7 w-7 ${colors.icon}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-gray-900 text-lg truncate">{pageConfig.title}</h3>
                  <p className="text-xs text-gray-500 font-mono">/{pageConfig.slug}</p>
                </div>
              </div>

              <p className="text-sm text-gray-600 mb-4 line-clamp-2">{pageConfig.description}</p>

              {/* Status & Stats */}
              <div className="flex items-center justify-between mb-4">
                {isConfigured ? (
                  isPublished ? (
                    <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold bg-green-100 text-green-700 border border-green-200 shadow-sm">
                      <CheckCircleSolid className="h-4 w-4 mr-1.5" />Live
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700 border border-amber-200">
                      <ClockIcon className="h-4 w-4 mr-1.5" />Draft
                    </span>
                  )
                ) : (
                  <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-500 border border-gray-200">
                    <XCircleIcon className="h-4 w-4 mr-1.5" />Not Set
                  </span>
                )}
                {isConfigured && (
                  <span className="text-xs text-gray-500 flex items-center gap-1">
                    <DocumentTextIcon className="h-4 w-4" />{sectionCount} sections
                  </span>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2">
                <button onClick={() => onEditPage(pageConfig.slug)}
                  className={`flex-1 inline-flex items-center justify-center px-4 py-3 text-sm font-semibold rounded-xl transition-all ${
                    isConfigured
                      ? 'text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-lg hover:shadow-xl'
                      : 'text-blue-600 bg-white hover:bg-blue-50 border-2 border-blue-200'
                  }`}>
                  <PencilSquareIcon className="h-4 w-4 mr-2" />
                  {isConfigured ? 'Edit Content' : 'Setup Page'}
                </button>
                <a href={pageConfig.url} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center justify-center p-3 text-gray-500 bg-white rounded-xl hover:bg-gray-50 border border-gray-200 transition-colors"
                  title="View live page">
                  <ArrowTopRightOnSquareIcon className="h-5 w-5" />
                </a>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default WebsiteCMS;


// Page Editor Component
interface PageEditorProps {
  page: PageContent;
  pageConfig: typeof WEBSITE_PAGES[0] | undefined;
  onSave: () => void;
  onCancel: () => void;
  onChange: (field: string, value: any) => void;
  onSEOChange: (field: string, value: any) => void;
  onLoadContent: (content: { pageTitle: string; sections: any[]; seo: any }) => void;
  onResetToHardcoded: () => void;
  saving: boolean;
  showToast: (message: string, type: 'success' | 'error' | 'info' | 'warning') => void;
}

const PageEditor: React.FC<PageEditorProps> = ({
  page, pageConfig, onSave, onCancel, onChange, onSEOChange, onLoadContent, onResetToHardcoded, saving, showToast,
}) => {
  const [activeSection, setActiveSection] = useState<'sections' | 'content' | 'seo'>('sections');
  const [loadingFromWebsite, setLoadingFromWebsite] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState<Set<number>>(new Set());

  const toggleSectionCollapse = (index: number) => {
    setCollapsedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const collapseAllSections = () => {
    if (page.sections) {
      setCollapsedSections(new Set(page.sections.map((_, i) => i)));
    }
  };

  const expandAllSections = () => {
    setCollapsedSections(new Set());
  };

  useEffect(() => { setHasChanges(true); }, [page]);

  const handleResetToHardcoded = async () => {
    if (!window.confirm('⚠️ This will DELETE all CMS content for this page.\n\nThe website will show default content instead.\n\nContinue?')) return;
    setResetting(true);
    try { await onResetToHardcoded(); } finally { setResetting(false); }
  };

  const handleLoadFromWebsite = () => {
    const websiteContent = getWebsiteContent(page.pageSlug);
    if (!websiteContent) { showToast('No website content found for this page', 'warning'); return; }
    if (!window.confirm('📥 This will load all content from the website template.\n\nYour current edits will be replaced. Continue?')) return;
    setLoadingFromWebsite(true);
    const mappedSections = websiteContent.sections.map((section: WebsiteSection) => ({
      id: section.id, type: section.type, title: section.title || '', subtitle: section.subtitle || '',
      description: section.description || '', content: section.content || '', image: section.image || '',
      items: section.items?.map((item: WebsiteSectionItem) => ({
        id: item.id, title: item.title, description: item.description, icon: item.icon || '',
        image: item.image || '', link: item.link || '', features: item.features || [], order: item.order
      })) || [],
      features: section.features || [], order: section.order
    }));
    onLoadContent({
      pageTitle: websiteContent.pageTitle, sections: mappedSections,
      seo: { metaTitle: websiteContent.seo.metaTitle, metaDescription: websiteContent.seo.metaDescription,
        keywords: websiteContent.seo.keywords, ogTitle: websiteContent.seo.ogTitle, ogDescription: websiteContent.seo.ogDescription }
    });
    setLoadingFromWebsite(false);
    showToast('Content loaded successfully! You can now edit the sections.', 'success');
  };

  const handleSectionChange = (index: number, field: string, value: any) => {
    const updatedSections = [...(page.sections || [])];
    updatedSections[index] = { ...updatedSections[index], [field]: value };
    onChange('sections', updatedSections);
  };

  const addSection = (type: string) => {
    const newSection = { id: `section-${Date.now()}`, type, title: '', subtitle: '', description: '', items: [], order: (page.sections?.length || 0) + 1 };
    onChange('sections', [...(page.sections || []), newSection]);
    showToast('New section added!', 'info');
  };

  const removeSection = (index: number) => {
    if (!window.confirm('Delete this section?')) return;
    const updatedSections = [...(page.sections || [])];
    updatedSections.splice(index, 1);
    onChange('sections', updatedSections);
    showToast('Section removed', 'info');
  };

  const moveSection = (index: number, direction: 'up' | 'down') => {
    const updatedSections = [...(page.sections || [])];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= updatedSections.length) return;
    [updatedSections[index], updatedSections[newIndex]] = [updatedSections[newIndex], updatedSections[index]];
    onChange('sections', updatedSections);
  };

  const seoScore = (() => {
    let score = 0;
    if (page.seo?.metaTitle && page.seo.metaTitle.length >= 30) score += 25;
    if (page.seo?.metaDescription && page.seo.metaDescription.length >= 100) score += 25;
    if (page.seo?.keywords && page.seo.keywords.length >= 3) score += 25;
    if (page.seo?.ogTitle && page.seo?.ogDescription) score += 25;
    return score;
  })();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Draft Warning Banner */}
      {page.status !== 'published' && (
        <div className="bg-amber-50 border-b border-amber-200 px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <ExclamationTriangleIcon className="h-5 w-5 text-amber-600" />
              <span className="text-amber-800 font-medium">
                This page is in <strong>{page.status || 'draft'}</strong> mode. Changes will NOT appear on the website until you set status to "Published".
              </span>
            </div>
            <button 
              onClick={() => onChange('status', 'published')}
              className="px-4 py-1.5 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm font-semibold transition-colors"
            >
              Publish Now
            </button>
          </div>
        </div>
      )}
      {/* Header */}
      <div className="bg-white/90 backdrop-blur-xl border-b border-gray-200 shadow-sm sticky top-0 z-20">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button onClick={onCancel} className="inline-flex items-center px-4 py-2.5 text-gray-700 hover:bg-gray-100 rounded-xl transition-colors font-medium">
                <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Back
              </button>
              <div className="h-8 w-px bg-gray-300" />
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-xl font-bold text-gray-900">{pageConfig?.title || page.pageTitle}</h1>
                  {hasChanges && <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">Unsaved</span>}
                </div>
                <p className="text-sm text-gray-500 font-mono">/{page.pageSlug}</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {page._id && (
                <button onClick={handleResetToHardcoded} disabled={resetting}
                  className="inline-flex items-center px-4 py-2.5 border border-red-200 bg-red-50 text-red-700 rounded-xl hover:bg-red-100 disabled:opacity-50 transition-colors font-medium">
                  <ArrowPathIcon className="h-4 w-4 mr-2" />{resetting ? 'Resetting...' : 'Reset'}
                </button>
              )}
              <button onClick={handleLoadFromWebsite} disabled={loadingFromWebsite}
                className="inline-flex items-center px-4 py-2.5 border-2 border-blue-200 bg-blue-50 text-blue-700 rounded-xl hover:bg-blue-100 disabled:opacity-50 font-semibold transition-colors">
                <CloudArrowDownIcon className="h-5 w-5 mr-2" />{loadingFromWebsite ? 'Loading...' : 'Load from Website'}
              </button>
              <select value={page.status} onChange={(e) => onChange('status', e.target.value)}
                className={`px-4 py-2.5 border-2 rounded-xl text-sm font-semibold transition-colors ${
                  page.status === 'published' ? 'border-green-200 bg-green-50 text-green-700' : 'border-gray-200 bg-white text-gray-700'
                }`}>
                <option value="draft">📝 Draft</option>
                <option value="published">✅ Published</option>
                <option value="archived">📦 Archived</option>
              </select>
              <button onClick={() => setShowPreview(!showPreview)}
                className="inline-flex items-center px-4 py-2.5 border border-gray-200 bg-white text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-medium">
                <EyeIcon className="h-4 w-4 mr-2" />Preview
              </button>
              <button onClick={onSave} disabled={saving}
                className="inline-flex items-center px-6 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 font-semibold shadow-lg hover:shadow-xl transition-all">
                {saving ? (
                  <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />Saving...</>
                ) : (
                  <><CheckIcon className="h-5 w-5 mr-2" />Save Changes</>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="px-6 bg-gradient-to-r from-gray-50 to-slate-50 border-t border-gray-100">
          <nav className="flex gap-1 -mb-px">
            {[
              { id: 'sections', label: 'Content Sections', icon: DocumentTextIcon, count: page.sections?.length || 0 },
              { id: 'content', label: 'Page Settings', icon: AdjustmentsHorizontalIcon },
              { id: 'seo', label: 'SEO Settings', icon: MagnifyingGlassIcon, badge: `${seoScore}%` },
            ].map((tab) => (
              <button key={tab.id} onClick={() => setActiveSection(tab.id as any)}
                className={`flex items-center gap-2 py-4 px-5 font-medium text-sm border-b-2 transition-all ${
                  activeSection === tab.id
                    ? 'border-blue-500 text-blue-600 bg-white'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-white/50'
                }`}>
                <tab.icon className="h-4 w-4" />
                {tab.label}
                {tab.count !== undefined && (
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                    activeSection === tab.id ? 'bg-blue-100 text-blue-600' : 'bg-gray-200 text-gray-600'
                  }`}>{tab.count}</span>
                )}
                {tab.badge && (
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                    seoScore >= 75 ? 'bg-green-100 text-green-600' : seoScore >= 50 ? 'bg-amber-100 text-amber-600' : 'bg-red-100 text-red-600'
                  }`}>{tab.badge}</span>
                )}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      <div className="p-6">
        {activeSection === 'sections' && (
          <div className="space-y-6">
            {/* Add Section Button */}
            <div className="bg-white rounded-2xl border-2 border-dashed border-gray-300 p-6 hover:border-blue-400 transition-colors">
              <div className="text-center">
                <h3 className="font-semibold text-gray-700 mb-4">Add New Section</h3>
                <div className="flex flex-wrap justify-center gap-2">
                  {['hero', 'content', 'features-grid', 'services-grid', 'alternating', 'testimonials', 'contact-info'].map((type) => (
                    <button key={type} onClick={() => addSection(type)}
                      className="px-4 py-2 bg-gray-100 hover:bg-blue-100 text-gray-700 hover:text-blue-700 rounded-lg text-sm font-medium transition-colors capitalize">
                      + {type.replace('-', ' ')}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Sections List */}
            {(!page.sections || page.sections.length === 0) ? (
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-12 text-center border-2 border-blue-100">
                <DocumentTextIcon className="h-16 w-16 text-blue-300 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-800 mb-2">No Sections Yet</h3>
                <p className="text-gray-600 mb-6">Click "Load from Website" above to import content, or add sections manually.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Collapse/Expand All Controls */}
                <div className="flex items-center justify-between bg-white rounded-xl px-4 py-2 border border-gray-200">
                  <span className="text-sm text-gray-600">{page.sections.length} section{page.sections.length !== 1 ? 's' : ''}</span>
                  <div className="flex items-center gap-2">
                    <button onClick={expandAllSections} className="px-3 py-1.5 text-xs text-blue-600 hover:bg-blue-50 rounded-lg transition-colors font-medium">
                      Expand All
                    </button>
                    <button onClick={collapseAllSections} className="px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-100 rounded-lg transition-colors font-medium">
                      Collapse All
                    </button>
                  </div>
                </div>

                {page.sections.map((section: any, index: number) => (
                  <div key={section.id || index} className="bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow overflow-hidden">
                    <div 
                      className="bg-gradient-to-r from-gray-50 to-slate-50 px-6 py-4 border-b border-gray-100 flex items-center justify-between cursor-pointer"
                      onClick={() => toggleSectionCollapse(index)}
                    >
                      <div className="flex items-center gap-3">
                        <button 
                          className="p-1 text-gray-400 hover:text-gray-600 transition-transform"
                          style={{ transform: collapsedSections.has(index) ? 'rotate(-90deg)' : 'rotate(0deg)' }}
                        >
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                        <span className="w-8 h-8 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center text-sm font-bold">{index + 1}</span>
                        <div>
                          <span className="font-semibold text-gray-800 capitalize">{section.type?.replace('-', ' ') || 'Section'}</span>
                          {section.title && <span className="text-gray-500 ml-2">— {section.title}</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                        <button onClick={() => moveSection(index, 'up')} disabled={index === 0}
                          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-30 transition-colors">
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" /></svg>
                        </button>
                        <button onClick={() => moveSection(index, 'down')} disabled={index === page.sections.length - 1}
                          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-30 transition-colors">
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                        </button>
                        <button onClick={() => removeSection(index)}
                          className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    {/* Collapsible Content */}
                    <div 
                      className={`transition-all duration-300 ease-in-out overflow-hidden ${
                        collapsedSections.has(index) ? 'max-h-0' : 'max-h-[2000px]'
                      }`}
                    >
                    <div className="p-6 space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Title
                            <span className="ml-2 text-xs text-blue-600 font-normal">✨ Rich Text with Colors</span>
                          </label>
                          <InlineRichTextEditor
                            content={section.title || ''}
                            onChange={(value) => handleSectionChange(index, 'title', value)}
                            placeholder="Section title"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Subtitle
                            <span className="ml-2 text-xs text-blue-600 font-normal">✨ Rich Text with Colors</span>
                          </label>
                          <InlineRichTextEditor
                            content={section.subtitle || ''}
                            onChange={(value) => handleSectionChange(index, 'subtitle', value)}
                            placeholder="Section subtitle"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Description
                          <span className="ml-2 text-xs text-blue-600 font-normal">✨ Rich Text Editor</span>
                        </label>
                        <RichTextEditor
                          content={section.description || ''}
                          onChange={(value) => handleSectionChange(index, 'description', value)}
                          placeholder="Brief description for this section..."
                          minHeight="100px"
                        />
                      </div>
                      {/* Content Field - Full text content with Rich Text Editor */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Content (Full Text)
                          <span className="ml-2 text-xs text-blue-600 font-normal">✨ Rich Text Editor</span>
                        </label>
                        <RichTextEditor
                          content={section.content || ''}
                          onChange={(value) => handleSectionChange(index, 'content', value)}
                          placeholder="Write your detailed content here... Use the toolbar to format text, add headings, lists, and links."
                          minHeight="200px"
                        />
                      </div>
                      {section.image !== undefined && (
                        <ImageUploader
                          value={section.image || ''}
                          onChange={(url) => handleSectionChange(index, 'image', url)}
                          label="Section Image"
                          placeholder="img/example.png or https://..."
                        />
                      )}
                      {/* Features List - EDITABLE */}
                      {(section.features !== undefined || section.type === 'features-grid') && (
                        <div className="bg-green-50 rounded-xl p-4 border border-green-200">
                          <div className="flex items-center justify-between mb-2">
                            <p className="text-sm font-bold text-green-700">Features ({section.features?.length || 0})</p>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Features (comma separated)</label>
                            <input
                              type="text"
                              value={section.features?.join(', ') || ''}
                              onChange={(e) => {
                                const features = e.target.value.split(',').map((f: string) => f.trim()).filter(Boolean);
                                handleSectionChange(index, 'features', features);
                              }}
                              className="w-full px-3 py-2 border border-green-200 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white"
                              placeholder="Feature 1, Feature 2, Feature 3"
                            />
                          </div>
                          {section.features && section.features.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-3">
                              {section.features.map((feature: string, fi: number) => (
                                <span key={fi} className="px-3 py-1.5 bg-white border border-green-200 rounded-lg text-sm text-green-700 flex items-center gap-1">
                                  ✓ {feature}
                                  <button
                                    onClick={() => {
                                      const updatedFeatures = [...section.features];
                                      updatedFeatures.splice(fi, 1);
                                      handleSectionChange(index, 'features', updatedFeatures);
                                    }}
                                    className="ml-1 text-green-400 hover:text-red-500"
                                  >
                                    ×
                                  </button>
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                      {/* Items with Full Details - EDITABLE - Always show for all sections */}
                      <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                        <div className="flex items-center justify-between mb-3">
                          <p className="text-sm font-bold text-blue-700">
                            <PlusIcon className="h-4 w-4 inline mr-1" />
                            Items ({section.items?.length || 0})
                          </p>
                          <button
                            onClick={() => {
                              const currentItems = section.items || [];
                              const newItem = {
                                id: `item-${Date.now()}`,
                                title: 'New Item',
                                description: 'Item description',
                                icon: '',
                                image: '',
                                link: '',
                                features: [],
                                order: currentItems.length + 1
                              };
                              handleSectionChange(index, 'items', [...currentItems, newItem]);
                            }}
                            className="px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 transition-colors"
                          >
                            + Add Item
                          </button>
                        </div>
                        {(!section.items || section.items.length === 0) ? (
                          <div className="text-center py-6 text-gray-500">
                            <p className="text-sm">No items yet. Click "+ Add Item" to add content items like services, features, or advantages.</p>
                          </div>
                        ) : (
                          <div className="space-y-4 max-h-[600px] overflow-y-auto">
                            {section.items.map((item: any, itemIndex: number) => (
                              <div key={item.id || itemIndex} className="bg-white rounded-xl p-4 border border-blue-100 shadow-sm">
                                <div className="flex items-center justify-between mb-3">
                                  <div className="flex items-center gap-2">
                                    <span className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold">{itemIndex + 1}</span>
                                    <span className="text-sm font-medium text-gray-700">Item {itemIndex + 1}</span>
                                  </div>
                                  <button
                                    onClick={() => {
                                      if (window.confirm('Delete this item?')) {
                                        const updatedItems = [...section.items];
                                        updatedItems.splice(itemIndex, 1);
                                        handleSectionChange(index, 'items', updatedItems);
                                      }
                                    }}
                                    className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                  >
                                    <XMarkIcon className="h-4 w-4" />
                                  </button>
                                </div>
                                
                                {/* Item Title with Rich Text */}
                                <div className="mb-3">
                                  <label className="block text-xs font-medium text-gray-600 mb-1">
                                    Title
                                    <span className="ml-1 text-blue-500">✨ Rich Text</span>
                                  </label>
                                  <InlineRichTextEditor
                                    content={item.title || ''}
                                    onChange={(value) => {
                                      const updatedItems = [...section.items];
                                      updatedItems[itemIndex] = { ...updatedItems[itemIndex], title: value };
                                      handleSectionChange(index, 'items', updatedItems);
                                    }}
                                    placeholder="Item title"
                                  />
                                </div>

                                {/* Item Description with Rich Text */}
                                <div className="mb-3">
                                  <label className="block text-xs font-medium text-gray-600 mb-1">
                                    Description
                                    <span className="ml-1 text-blue-500">✨ Rich Text</span>
                                  </label>
                                  <RichTextEditor
                                    content={item.description || ''}
                                    onChange={(value) => {
                                      const updatedItems = [...section.items];
                                      updatedItems[itemIndex] = { ...updatedItems[itemIndex], description: value };
                                      handleSectionChange(index, 'items', updatedItems);
                                    }}
                                    placeholder="Item description..."
                                    minHeight="80px"
                                  />
                                </div>

                                {/* Item Icon & Link */}
                                <div className="grid grid-cols-2 gap-3 mb-3">
                                  <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Icon</label>
                                    <input
                                      type="text"
                                      value={item.icon || ''}
                                      onChange={(e) => {
                                        const updatedItems = [...section.items];
                                        updatedItems[itemIndex] = { ...updatedItems[itemIndex], icon: e.target.value };
                                        handleSectionChange(index, 'items', updatedItems);
                                      }}
                                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                      placeholder="Icon name (e.g., rent, premium)"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Link</label>
                                    <input
                                      type="text"
                                      value={item.link || ''}
                                      onChange={(e) => {
                                        const updatedItems = [...section.items];
                                        updatedItems[itemIndex] = { ...updatedItems[itemIndex], link: e.target.value };
                                        handleSectionChange(index, 'items', updatedItems);
                                      }}
                                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                      placeholder="/services or https://..."
                                    />
                                  </div>
                                </div>

                                {/* Item Image */}
                                <div className="mb-3">
                                  <label className="block text-xs font-medium text-gray-600 mb-1">Image URL</label>
                                  <input
                                    type="text"
                                    value={item.image || ''}
                                    onChange={(e) => {
                                      const updatedItems = [...section.items];
                                      updatedItems[itemIndex] = { ...updatedItems[itemIndex], image: e.target.value };
                                      handleSectionChange(index, 'items', updatedItems);
                                    }}
                                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="img/service_1.png or https://..."
                                  />
                                </div>

                                {/* Item Features */}
                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">Features (comma separated)</label>
                                  <input
                                    type="text"
                                    value={item.features?.join(', ') || ''}
                                    onChange={(e) => {
                                      const updatedItems = [...section.items];
                                      updatedItems[itemIndex] = { 
                                        ...updatedItems[itemIndex], 
                                        features: e.target.value.split(',').map((f: string) => f.trim()).filter(Boolean)
                                      };
                                      handleSectionChange(index, 'items', updatedItems);
                                    }}
                                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="Feature 1, Feature 2, Feature 3"
                                  />
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    </div>{/* End Collapsible Content */}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeSection === 'content' && (
          <div className="max-w-2xl mx-auto space-y-6">
            <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
              <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                <AdjustmentsHorizontalIcon className="h-5 w-5 text-blue-600" />
                Page Settings
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Page Title</label>
                  <input type="text" value={page.pageTitle || ''} onChange={(e) => onChange('pageTitle', e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Page Slug</label>
                  <input type="text" value={page.pageSlug || ''} disabled
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl bg-gray-50 text-gray-500 font-mono" />
                  <p className="text-xs text-gray-500 mt-1">The slug cannot be changed after creation</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select value={page.status || 'draft'} onChange={(e) => onChange('status', e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeSection === 'seo' && (
          <div className="max-w-2xl mx-auto space-y-6">
            {/* SEO Score Card */}
            <div className={`rounded-2xl p-6 border-2 ${seoScore >= 75 ? 'bg-green-50 border-green-200' : seoScore >= 50 ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200'}`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-gray-800">SEO Score</h3>
                <span className={`text-3xl font-bold ${seoScore >= 75 ? 'text-green-600' : seoScore >= 50 ? 'text-amber-600' : 'text-red-600'}`}>{seoScore}%</span>
              </div>
              <ProgressBar progress={seoScore} />
              <p className="text-sm text-gray-600 mt-3">
                {seoScore >= 75 ? '✅ Great! Your SEO settings are well optimized.' : seoScore >= 50 ? '⚠️ Good start, but there\'s room for improvement.' : '❌ Your SEO needs attention. Fill in the fields below.'}
              </p>
            </div>

            {/* SEO Fields */}
            <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm space-y-4">
              <h3 className="font-bold text-gray-800 flex items-center gap-2">
                <MagnifyingGlassIcon className="h-5 w-5 text-blue-600" />
                SEO Settings
              </h3>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Meta Title <span className="text-gray-400">({page.seo?.metaTitle?.length || 0}/60)</span></label>
                <input type="text" value={page.seo?.metaTitle || ''} onChange={(e) => onSEOChange('metaTitle', e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="Page title for search engines" maxLength={60} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Meta Description <span className="text-gray-400">({page.seo?.metaDescription?.length || 0}/160)</span></label>
                <textarea value={page.seo?.metaDescription || ''} onChange={(e) => onSEOChange('metaDescription', e.target.value)}
                  rows={3} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="Brief description for search results" maxLength={160} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Keywords <span className="text-gray-400">(comma separated)</span></label>
                <input type="text" value={page.seo?.keywords?.join(', ') || ''} onChange={(e) => onSEOChange('keywords', e.target.value.split(',').map((k: string) => k.trim()).filter(Boolean))}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="keyword1, keyword2, keyword3" />
              </div>
              <div className="pt-4 border-t border-gray-100">
                <h4 className="font-medium text-gray-700 mb-3">Open Graph (Social Sharing)</h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">OG Title</label>
                    <input type="text" value={page.seo?.ogTitle || ''} onChange={(e) => onSEOChange('ogTitle', e.target.value)}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="Title for social media shares" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">OG Description</label>
                    <textarea value={page.seo?.ogDescription || ''} onChange={(e) => onSEOChange('ogDescription', e.target.value)}
                      rows={2} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="Description for social media shares" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Live Preview Modal */}
      {showPreview && (
        <LivePreview
          pageTitle={page.pageTitle}
          pageSlug={page.pageSlug}
          sections={page.sections || []}
          onClose={() => setShowPreview(false)}
        />
      )}
    </div>
  );
};


// Contact Info Tab Component
const ContactInfoTab: React.FC<{ showToast: (message: string, type: 'success' | 'error' | 'info' | 'warning') => void }> = ({ showToast }) => {
  const [contactInfo, setContactInfo] = useState<WebsiteContactInfo[]>(WEBSITE_CONTACT_INFO);
  const [saving, setSaving] = useState(false);

  const handleChange = (index: number, field: string, value: any) => {
    const updated = [...contactInfo];
    updated[index] = { ...updated[index], [field]: value };
    setContactInfo(updated);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Save each contact info item individually
      for (const info of contactInfo) {
        const contactData = {
          type: info.type as 'phone' | 'email' | 'address' | 'hours' | 'social',
          label: info.label,
          value: info.value,
          icon: info.icon,
          order: info.order,
          active: info.active,
        };
        await pageService.createContactInfo(contactData);
      }
      showToast('Contact information saved!', 'success');
    } catch (error) {
      showToast('Failed to save contact info', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-100">
        <div className="flex items-center gap-3 mb-2">
          <PhoneIcon className="h-6 w-6 text-blue-600" />
          <h2 className="text-xl font-bold text-gray-800">Contact Information</h2>
        </div>
        <p className="text-gray-600">Manage your business contact details displayed across the website.</p>
      </div>

      <div className="space-y-4">
        {contactInfo.map((info, index) => (
          <div key={index} className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                  {info.type === 'phone' && <PhoneIcon className="h-5 w-5 text-blue-600" />}
                  {info.type === 'email' && <DocumentTextIcon className="h-5 w-5 text-blue-600" />}
                  {info.type === 'address' && <HomeIcon className="h-5 w-5 text-blue-600" />}
                  {info.type === 'hours' && <ClockIcon className="h-5 w-5 text-blue-600" />}
                  {info.type === 'social' && <GlobeAltIcon className="h-5 w-5 text-blue-600" />}
                </span>
                <span className="font-semibold text-gray-800 capitalize">{info.type}</span>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={info.active} onChange={(e) => handleChange(index, 'active', e.target.checked)}
                  className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                <span className="text-sm text-gray-600">Active</span>
              </label>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Label</label>
                <input type="text" value={info.label} onChange={(e) => handleChange(index, 'label', e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Value</label>
                <input type="text" value={info.value} onChange={(e) => handleChange(index, 'value', e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="flex justify-end">
        <button onClick={handleSave} disabled={saving}
          className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 font-semibold shadow-lg hover:shadow-xl transition-all">
          {saving ? 'Saving...' : 'Save Contact Info'}
        </button>
      </div>
    </div>
  );
};


// Site Settings Tab Component
const SiteSettingsTab: React.FC<{ showToast: (message: string, type: 'success' | 'error' | 'info' | 'warning') => void }> = ({ showToast }) => {
  const [settings, setSettings] = useState({
    siteName: 'TAK8 Car Rental',
    tagline: 'Drive Your Journey, Leave the Rest to Us!',
    logo: 'img/logo.png',
    favicon: 'favicon.ico',
    primaryColor: '#2563eb',
    secondaryColor: '#7c3aed',
  });
  const [saving, setSaving] = useState(false);

  const handleChange = (field: string, value: string) => {
    setSettings({ ...settings, [field]: value });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Save each setting individually using the updateSetting method
      await pageService.updateSetting('siteName', settings.siteName);
      await pageService.updateSetting('tagline', settings.tagline);
      await pageService.updateSetting('logo', settings.logo);
      await pageService.updateSetting('favicon', settings.favicon);
      await pageService.updateSetting('primaryColor', settings.primaryColor);
      await pageService.updateSetting('secondaryColor', settings.secondaryColor);
      showToast('Site settings saved!', 'success');
    } catch (error) {
      showToast('Failed to save settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-6 border border-purple-100">
        <div className="flex items-center gap-3 mb-2">
          <WrenchScrewdriverIcon className="h-6 w-6 text-purple-600" />
          <h2 className="text-xl font-bold text-gray-800">Site Settings</h2>
        </div>
        <p className="text-gray-600">Configure global website settings and branding.</p>
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Site Name</label>
            <input type="text" value={settings.siteName} onChange={(e) => handleChange('siteName', e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tagline</label>
            <input type="text" value={settings.tagline} onChange={(e) => handleChange('tagline', e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Logo Path</label>
            <input type="text" value={settings.logo} onChange={(e) => handleChange('logo', e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Favicon Path</label>
            <input type="text" value={settings.favicon} onChange={(e) => handleChange('favicon', e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Primary Color</label>
            <div className="flex gap-2">
              <input type="color" value={settings.primaryColor} onChange={(e) => handleChange('primaryColor', e.target.value)}
                className="w-12 h-10 rounded-lg border border-gray-200 cursor-pointer" />
              <input type="text" value={settings.primaryColor} onChange={(e) => handleChange('primaryColor', e.target.value)}
                className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Secondary Color</label>
            <div className="flex gap-2">
              <input type="color" value={settings.secondaryColor} onChange={(e) => handleChange('secondaryColor', e.target.value)}
                className="w-12 h-10 rounded-lg border border-gray-200 cursor-pointer" />
              <input type="text" value={settings.secondaryColor} onChange={(e) => handleChange('secondaryColor', e.target.value)}
                className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono" />
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button onClick={handleSave} disabled={saving}
          className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 font-semibold shadow-lg hover:shadow-xl transition-all">
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
};